import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { TimerDisplay } from "@/components/TimerDisplay";
import { Play, Pause, RotateCcw, FastForward, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { playSound } from "@/lib/sounds";
import { useTimerSocket } from "@/lib/useTimerSocket";

const POLL_MS = 1000;

export default function TimerView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [timer, setTimer] = useState(null);
  const [err, setErr] = useState("");
  const lastPhaseRef = useRef(null);
  const lastCyclesRef = useRef(null);
  const tickRef = useRef(null);
  // Local remaining counter for smooth ticking between server polls
  const [localRemaining, setLocalRemaining] = useState(0);

  const fetchTimer = async () => {
    try {
      const { data } = await api.get(`/timers/${id}`);
      setTimer(data);
      setLocalRemaining(data.remaining_seconds);
      // Detect phase change → play sound
      if (lastPhaseRef.current && lastPhaseRef.current !== data.phase) {
        playSound(data.sound);
        toast.success(`Phase complete · now: ${data.phase.replace("_", " ")}`);
      }
      // Detect cycle increment too (in case of skip from work)
      if (lastCyclesRef.current !== null && data.completed_cycles > lastCyclesRef.current && data.phase !== "work") {
        // already handled above via phase change
      }
      lastPhaseRef.current = data.phase;
      lastCyclesRef.current = data.completed_cycles;
    } catch (e) {
      if (e.response?.status === 403) {
        setErr("This timer belongs to another user.");
      } else if (e.response?.status === 404) {
        setErr("Timer not found.");
      } else {
        setErr("Could not load timer.");
      }
    }
  };

  useEffect(() => {
    fetchTimer();
    const interval = setInterval(fetchTimer, POLL_MS * 3);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Realtime push updates via WebSocket (instant start/pause/reset/skip propagation)
  useTimerSocket(timer?.share_code, (next) => {
    if (lastPhaseRef.current && lastPhaseRef.current !== next.phase) {
      playSound(next.sound);
      toast.success(`Phase complete · now: ${next.phase.replace("_", " ")}`);
    }
    lastPhaseRef.current = next.phase;
    lastCyclesRef.current = next.completed_cycles;
    setTimer(next);
    setLocalRemaining(next.remaining_seconds);
  });

  // Local 1Hz tick for smoother countdown
  useEffect(() => {
    clearInterval(tickRef.current);
    if (timer?.is_running) {
      tickRef.current = setInterval(() => {
        setLocalRemaining((r) => (r > 0 ? r - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(tickRef.current);
  }, [timer?.is_running, timer?.phase, timer?.id]);

  const action = async (verb) => {
    try {
      const { data } = await api.post(`/timers/${id}/${verb}`);
      setTimer(data);
      setLocalRemaining(data.remaining_seconds);
      lastPhaseRef.current = data.phase;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const copyShare = () => {
    if (!timer?.share_code) return;
    const link = `${window.location.origin}/t/${timer.share_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Share link copied to clipboard");
  };

  if (err) {
    return (
      <div className="min-h-screen bg-background bg-grid scanlines">
        <Navbar />
        <main className="mx-auto max-w-3xl px-6 py-24 text-center">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-destructive mb-3">// error</div>
          <h1 className="font-display font-bold text-3xl">{err}</h1>
          <Button onClick={() => navigate("/dashboard")} className="mt-8 rounded-none font-mono uppercase tracking-widest">
            Back to dashboard
          </Button>
        </main>
      </div>
    );
  }

  if (!timer) {
    return (
      <div className="min-h-screen bg-background bg-grid scanlines">
        <Navbar />
        <main className="mx-auto max-w-3xl px-6 py-24 text-center font-mono text-sm text-muted-foreground">
          <span className="caret">// loading_timer</span>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background bg-grid scanlines">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="section-label mb-3">timer · {timer.id.slice(0, 6)}</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight" data-testid="timer-name">{timer.name}</h1>
            <p className="mt-2 font-mono text-sm text-muted-foreground">
              {">"} {timer.work_minutes}m focus · {timer.short_break_minutes}m short · {timer.long_break_minutes}m long · every {timer.long_break_interval} cycles
            </p>
          </div>
          {timer.shared && (
            <div className="border border-border p-4 font-mono text-xs">
              <div className="text-muted-foreground uppercase tracking-[0.3em] text-[10px] mb-2">// share_code</div>
              <div className="flex items-center gap-3">
                <div className="font-bold text-primary text-2xl tracking-widest" data-testid="timer-share-code">{timer.share_code}</div>
                <button onClick={copyShare} className="p-2 border border-border hover:border-primary hover:text-primary" data-testid="timer-share-copy">
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <a href={`/t/${timer.share_code}`} target="_blank" rel="noreferrer" className="p-2 border border-border hover:border-primary hover:text-primary" data-testid="timer-share-open">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 flex flex-col items-center">
          <TimerDisplay
            remaining={localRemaining}
            total={timer.total_seconds}
            phase={timer.phase}
            completedCycles={timer.completed_cycles}
            longBreakInterval={timer.long_break_interval}
          />

          <div className="mt-12 flex flex-wrap items-center justify-center gap-3" data-testid="timer-controls">
            {!timer.is_running ? (
              <Button onClick={() => action("start")} className="rounded-none font-mono uppercase tracking-widest h-12 px-8" data-testid="timer-start-btn">
                <Play className="mr-2 h-4 w-4" /> Start
              </Button>
            ) : (
              <Button onClick={() => action("pause")} variant="outline" className="rounded-none border-border font-mono uppercase tracking-widest h-12 px-8" data-testid="timer-pause-btn">
                <Pause className="mr-2 h-4 w-4" /> Pause
              </Button>
            )}
            <Button onClick={() => action("skip")} variant="outline" className="rounded-none border-border font-mono uppercase tracking-widest h-12 px-6" data-testid="timer-skip-btn">
              <FastForward className="mr-2 h-4 w-4" /> Skip Phase
            </Button>
            <Button onClick={() => action("reset")} variant="outline" className="rounded-none border-border font-mono uppercase tracking-widest h-12 px-6" data-testid="timer-reset-btn">
              <RotateCcw className="mr-2 h-4 w-4" /> Reset
            </Button>
            <Button type="button" onClick={() => playSound(timer.sound)} variant="ghost" className="rounded-none font-mono uppercase tracking-widest h-12 px-6" data-testid="timer-sound-test-btn">
              Test Sound · {timer.sound}
            </Button>
          </div>

          <div className="mt-12 w-full max-w-2xl border border-border bg-card font-mono text-xs">
            <div className="border-b border-border px-4 py-2 flex items-center justify-between text-muted-foreground uppercase tracking-[0.3em] text-[10px]">
              <span>// session_log</span>
              <span className={timer.is_running ? "text-emerald-400" : "text-amber-400"}>{timer.is_running ? "RUNNING" : "PAUSED"}</span>
            </div>
            <div className="p-4 space-y-1">
              <div><span className="text-primary">{">"}</span> creator: <span className="text-foreground">{timer.creator_name}</span></div>
              <div><span className="text-primary">{">"}</span> phase: <span className="text-foreground">{timer.phase}</span></div>
              <div><span className="text-primary">{">"}</span> cycles_complete: <span className="text-foreground">{timer.completed_cycles}</span></div>
              <div><span className="text-primary">{">"}</span> sharing: <span className="text-foreground">{timer.shared ? "enabled" : "disabled"}</span></div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
